import { CHART_LINE_TYPE } from 'src/constants/chart';

import GenericDB from './generic-db';

/** @typedef {import('src/constants/chart').ChartLineType} ChartLineType */

/** @type {ChartLineType[]} */
const LINE_TYPES = Object.values(CHART_LINE_TYPE);

/** @param {ChartLineType} type */
function assertLineType(type) {
  if (!LINE_TYPES.includes(type)) {
    throw new Error(`Chart line type must be one of ${LINE_TYPES.join(', ')}; got ${type}`);
  }
}

/**
 * Phase and event lines share this collection, told apart by `type`.
 */
export default class ChartPhaseLinesDB extends GenericDB {
  constructor() {
    super('chartPhaseLines');
  }

  /**
   * Every line in a scope, split by type. The query cannot filter on `type` itself: Firestore
   * does not match documents missing the field, which would drop every phase line written before it.
   * A type this version does not know is left out of both lists.
   *
   * @param {string} clinic
   * @param {string} client
   * @param {{ scope: string, program?: string, target?: string }} chartLineScope
   * @returns {Promise<{ phaseLines: Object[], eventLines: Object[] }>}
   */
  async readLineScope(clinic, client, chartLineScope) {
    const query = [
      ['client', '==', client],
      ['clinic', '==', clinic],
      ['scope', '==', chartLineScope.scope],
    ];
    if (['program', 'target', 'subTarget'].includes(chartLineScope.scope)) {
      query.push(['program', '==', chartLineScope.program]);
    }
    if (['target', 'subTarget'].includes(chartLineScope.scope)) {
      query.push(['target', '==', chartLineScope.target]);
    }

    const lines = (await this.readAll(query))
      .map((line) => ({
        ...line,
        // Default historical lines as phase lines.
        type: line.type ?? CHART_LINE_TYPE.PHASE,
      }));

    return {
      phaseLines: lines.filter(({ type }) => type === CHART_LINE_TYPE.PHASE),
      eventLines: lines.filter(({ type }) => type === CHART_LINE_TYPE.EVENT),
    };
  } // readLineScope()

  /**
   * Refuses a line without a valid `type`, so no new line is written untyped.
   *
   * @param {Object} line
   * @param {...*} rest The rest of GenericDB.create's arguments
   * @throws {Error} If the line type is invalid.
   */
  create(line, ...rest) {
    assertLineType(line.type);
    return super.create(line, ...rest);
  } // create()

  /**
   * Refuses a line without a valid `type`, so an update never leaves a line untyped. A partial
   * update must include it too.
   *
   * @param {Object} line
   * @param {string} id
   * @param {...*} rest The rest of GenericDB.set's arguments
   * @throws {Error} If the line type is invalid.
   */
  set(line, id, ...rest) {
    assertLineType(line.type);
    return super.set(line, id, ...rest);
  } // set()
}
